import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useActivity } from './ActivityContext';

// Create a broadcast channel for cross-tab communication
const authChannel = typeof window !== 'undefined' ? new BroadcastChannel('auth_updates') : null;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const activity = useActivity();

  // Default users that will be available to all new visitors
  const defaultUsers = [
    { id: '1', username: 'trauma admin', password: 'JHiidj12££', role: 'admin', createdAt: new Date().toISOString() },
    { id: '2', username: 'user', password: 'password', role: 'user', createdAt: new Date().toISOString() }
  ];

  // Initialize with users from localStorage or default users
  const [users, setUsers] = useState(() => {
    try {
      const storedUsers = localStorage.getItem('trauma_users');
      if (storedUsers) {
        const parsed = JSON.parse(storedUsers);
        // Merge with default users, preserving any existing users
        const mergedUsers = [...defaultUsers];
        parsed.forEach(user => {
          if (!mergedUsers.some(u => u.username === user.username)) {
            mergedUsers.push(user);
          }
        });
        localStorage.setItem('trauma_users', JSON.stringify(mergedUsers));
        return mergedUsers;
      }
      // If no users in localStorage, save default users
      localStorage.setItem('trauma_users', JSON.stringify(defaultUsers));
      return defaultUsers;
    } catch (error) {
      console.error('Error loading users from localStorage:', error);
      // Save default users if there was an error
      localStorage.setItem('trauma_users', JSON.stringify(defaultUsers));
      return defaultUsers;
    }
  });

  // Function to update users and sync across tabs
  const updateUsers = useCallback((newUsers) => {
    localStorage.setItem('trauma_users', JSON.stringify(newUsers));
    setUsers(newUsers);
    // Broadcast the update to other tabs
    if (authChannel) {
      authChannel.postMessage({ type: 'USERS_UPDATED', users: newUsers });
    }
  }, []);

  // Listen for updates from other tabs
  useEffect(() => {
    if (!authChannel) return;

    const handleMessage = (event) => {
      if (event.data.type === 'USERS_UPDATED') {
        setUsers(event.data.users);
      }
    };

    authChannel.addEventListener('message', handleMessage);
    return () => {
      authChannel.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const authStatus = localStorage.getItem('trauma_auth');
    const userRole = localStorage.getItem('trauma_role');
    const userData = localStorage.getItem('trauma_user');
    
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setIsAdmin(userRole === 'admin');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
      
      // Initialize session for returning users
      if (activity && activity.initializeSession) {
        activity.initializeSession();
      }
    }
    setLoading(false);
  }, [activity]);

  const login = async (username, password) => {
    // Always get the latest users from localStorage to ensure we have the most current data
    const currentUsers = JSON.parse(localStorage.getItem('trauma_users') || '[]');
    
    // Find user in the most current user list
    const foundUser = currentUsers.find(user => 
      user.username === username && user.password === password
    );
    
    if (foundUser) {
      const user = {
        ...foundUser,
        loginTime: new Date().toISOString()
      };
      
      setIsAuthenticated(true);
      setIsAdmin(foundUser.role === 'admin');
      setCurrentUser(user);
      
      // Update the users state to ensure consistency
      setUsers(currentUsers);
      
      localStorage.setItem('trauma_auth', 'true');
      localStorage.setItem('trauma_role', foundUser.role);
      localStorage.setItem('trauma_user', JSON.stringify(user));
      
      // Initialize session
      if (activity && activity.initializeSession) {
        await activity.initializeSession();
      }
      
      return { success: true, role: foundUser.role };
    }
    
    return { success: false, error: 'Invalid credentials' };
  };

  // User management functions
  const addUser = (username, password, role = 'user') => {
    const newUser = {
      id: Date.now().toString(),
      username,
      password,
      role,
      createdAt: new Date().toISOString()
    };

    const updatedUsers = [...users, newUser];
    updateUsers(updatedUsers);
    return newUser;
  };

  const deleteUser = (userId) => {
    const updatedUsers = users.filter(user => user.id !== userId);
    updateUsers(updatedUsers);
  };

  const updateUserRole = (userId, newRole) => {
    const updatedUsers = users.map(user => 
      user.id === userId ? { ...user, role: newRole } : user
    );
    updateUsers(updatedUsers);
  };

  const logout = () => {
    // End activity session
    if (activity && activity.endSession) {
      activity.endSession();
    }
    
    setIsAuthenticated(false);
    setIsAdmin(false);
    setCurrentUser(null);
    
    localStorage.removeItem('trauma_auth');
    localStorage.removeItem('trauma_role');
    localStorage.removeItem('trauma_user');
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAdmin,
      currentUser,
      loading,
      users,
      login,
      logout,
      addUser,
      deleteUser,
      updateUserRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
